import { Activity, Streak } from '@/types';

export class ActivityCard {
  private element: HTMLElement;
  private onComplete: (activityId: string) => void;
  private onEdit: (activity: Activity) => void;
  private onDelete: (activityId: string) => void;

  constructor(
    private activity: Activity,
    private streak: Streak,
    private isCompletedToday: boolean,
    callbacks: {
      onComplete: (activityId: string) => void;
      onEdit: (activity: Activity) => void;
      onDelete: (activityId: string) => void;
    }
  ) {
    this.onComplete = callbacks.onComplete;
    this.onEdit = callbacks.onEdit;
    this.onDelete = callbacks.onDelete;
    this.element = document.createElement('div');
    this.render();
    this.attachEventListeners();
  }

  private render(): void {
    const categoryColors: Record<string, string> = {
      habit: '#10b981',
      study: '#3b82f6',
      fitness: '#f59e0b',
      work: '#8b5cf6',
      personal: '#ef4444'
    };

    const categoryIcons: Record<string, string> = {
      habit: '🎯',
      study: '📚',
      fitness: '💪',
      work: '💼',
      personal: '❤️'
    };

    this.element.className = 'card p-6';
    this.element.innerHTML = `
      <div class="flex items-start justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="activity-icon" style="background-color: ${categoryColors[this.activity.category]}">
            ${this.activity.icon || categoryIcons[this.activity.category]}
          </div>
          <div>
            <h3 class="font-semibold text-lg">${this.activity.name}</h3>
            ${this.activity.description ? `<p class="text-sm text-gray-600">${this.activity.description}</p>` : ''}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button class="btn btn-secondary btn-sm edit-btn" title="Edit">
            ✏️
          </button>
          <button class="btn btn-danger btn-sm delete-btn" title="Delete">
            🗑️
          </button>
        </div>
      </div>
      
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-4">
          <div class="streak-counter">
            🔥 ${this.streak.current} day streak
          </div>
          <div class="text-sm text-gray-500">
            Best: ${this.streak.longest} days
          </div>
        </div>
        <div class="text-sm text-gray-500">
          ${this.activity.pointsPerCompletion} points
        </div>
      </div>
      
      <div class="flex items-center justify-between">
        <div class="text-sm text-gray-500 capitalize">
          ${this.activity.targetFrequency} • ${this.activity.category}
        </div>
        <button 
          class="btn ${this.isCompletedToday ? 'btn-success' : 'btn-primary'} btn-sm complete-btn"
          ${this.isCompletedToday ? 'disabled' : ''}
        >
          ${this.isCompletedToday ? '✅ Completed' : '✨ Mark Complete'}
        </button>
      </div>
    `;
  }

  private attachEventListeners(): void {
    const completeBtn = this.element.querySelector('.complete-btn') as HTMLButtonElement;
    const editBtn = this.element.querySelector('.edit-btn') as HTMLButtonElement;
    const deleteBtn = this.element.querySelector('.delete-btn') as HTMLButtonElement;

    completeBtn?.addEventListener('click', () => {
      if (!this.isCompletedToday) {
        this.onComplete(this.activity.id);
      }
    });

    editBtn?.addEventListener('click', () => {
      this.onEdit(this.activity);
    });

    deleteBtn?.addEventListener('click', () => {
      if (confirm(`Are you sure you want to delete "${this.activity.name}"?`)) {
        this.onDelete(this.activity.id);
      }
    });
  }

  public updateActivity(activity: Activity, streak: Streak, isCompletedToday: boolean): void {
    this.activity = activity;
    this.streak = streak;
    this.isCompletedToday = isCompletedToday;
    this.render();
    this.attachEventListeners();
  }

  public getElement(): HTMLElement {
    return this.element;
  }
}